import type {
  CanonicalMarketType,
  Locale,
  Outcome,
  Period,
  StringTable,
} from '@geni/contracts';
import { resolveString } from './strings.js';

/**
 * Saying what a bet is, in the bettor's language, without a provider's help.
 *
 * The gap this closes was deliberate and is documented: the odds feed localises
 * market names into some languages and not others — the mock does en/es/pt-BR
 * but not ru/tr, mirroring real feeds — so a Turkish player saw English market
 * names however good our own tables were.
 *
 * What makes closing it possible is that the interesting half of a bet's name
 * is already OURS. `CanonicalMarketType` and `Outcome` are our enums, mapped at
 * the adapter boundary; the provider's own wording is only ever a display
 * string layered on top. So "Total goals: Over 2.5" can be composed from
 * structure in any language we have words for.
 *
 * What this deliberately does NOT do:
 *
 *   **Translate proper nouns.** Team and player names come through untouched.
 *   Nobody wants "Bellingham" localised, and a system that tried would produce
 *   nonsense in exactly the cases a bettor most needs to recognise.
 *
 *   **Invent a name for UNMAPPED.** A market we have not canonicalised has no
 *   structure to compose from, so these return undefined and the caller falls
 *   back to whatever the provider said. Guessing would be worse than English.
 *
 * The honest summary of the result: the gap narrows from "everything is
 * English" to "proper nouns and uncanonicalised markets are as the feed sent
 * them", which is where it should have been all along.
 */

export interface TermContext {
  /** Home team name, as the provider gave it. Never translated. */
  home?: string;
  /** Away team name. Never translated. */
  away?: string;
  /** Player name for a player market. Never translated. */
  player?: string;
  /** The handicap or total line, e.g. 2.5. */
  line?: number;
  period?: Period;
}

/**
 * Fill `{home}`, `{away}`, `{player}`, `{line}` in a template.
 *
 * A placeholder with nothing to fill it is left alone rather than replaced with
 * an empty string: "{player} to score" with no player reads as a bug, and
 * " to score" reads as a mystery.
 */
export function interpolate(template: string, values: Record<string, string | undefined>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => values[key] ?? whole);
}

function slot(context: TermContext): Record<string, string | undefined> {
  return {
    home: context.home,
    away: context.away,
    player: context.player,
    line: context.line === undefined ? undefined : String(context.line),
  };
}

/**
 * Whether a key exists in a locale's chain at all.
 *
 * `resolveString` returns the key itself when nothing has it, which is right
 * for display — a visible key is a bug report — but useless for deciding
 * whether to fall back to the provider's wording.
 */
function lookup(
  tables: ReadonlyMap<string, StringTable>,
  locale: Locale,
  key: string,
): string | undefined {
  const found = resolveString(tables, locale, key);
  return found === key ? undefined : found;
}

/** The market's name, or undefined when we have nothing better than the feed. */
export function localizeMarketName(args: {
  tables: ReadonlyMap<string, StringTable>;
  locale: Locale;
  type: CanonicalMarketType;
  context?: TermContext;
}): string | undefined {
  // No structure, so nothing to compose. The provider's wording stands.
  if (args.type === 'UNMAPPED') return undefined;

  const context = args.context ?? {};
  const template = lookup(args.tables, args.locale, `market.${args.type}`);
  if (template === undefined) return undefined;

  const name = interpolate(template, slot(context));

  // Half-time markets are the same market over a shorter period, so the period
  // is a suffix rather than fourteen more keys.
  if (context.period !== undefined && context.period !== 'FULL_TIME') {
    const period = lookup(args.tables, args.locale, `period.${context.period}`);
    if (period !== undefined) return `${name} (${period})`;
  }

  return name;
}

/** The selection's name, or undefined when the feed's wording should stand. */
export function localizeOutcome(args: {
  tables: ReadonlyMap<string, StringTable>;
  locale: Locale;
  outcome: Outcome;
  context?: TermContext;
}): string | undefined {
  const context = args.context ?? {};

  switch (args.outcome.kind) {
    case 'UNMAPPED':
      return undefined;

    case 'PARTICIPANT':
      // A person, not a phrase. The name is the label, and translating it would
      // be actively wrong.
      return context.player;

    case 'EXACT_SCORE': {
      const template = lookup(args.tables, args.locale, 'outcome.EXACT_SCORE');
      if (template === undefined) return undefined;
      // Deliberately not the {home}/{away} team-name slots: this is a score.
      return interpolate(template, {
        home: String(args.outcome.home),
        away: String(args.outcome.away),
      });
    }

    default: {
      const template = lookup(args.tables, args.locale, `outcome.${args.outcome.kind}`);
      if (template === undefined) return undefined;
      return interpolate(template, slot(context));
    }
  }
}

/**
 * The full "Market: Selection" label.
 *
 * Joined with a colon rather than composed into a sentence, for the same reason
 * the candidate labels always were: it works in every language, including the
 * ones we cannot form grammar for. Where the market name and the selection name
 * are the same string — player markets, where both are "Bellingham to score" —
 * only one is shown.
 */
export function localizeSelectionLabel(args: {
  tables: ReadonlyMap<string, StringTable>;
  locale: Locale;
  type: CanonicalMarketType;
  outcome: Outcome;
  context?: TermContext;
}): { marketName: string; selectionName: string; label: string } | undefined {
  const marketName = localizeMarketName(args);
  const selectionName = localizeOutcome(args);

  if (marketName === undefined || selectionName === undefined) return undefined;
  if (marketName === selectionName) {
    return { marketName, selectionName, label: selectionName };
  }

  return { marketName, selectionName, label: `${marketName}: ${selectionName}` };
}
